import React, { useState, useEffect } from 'react';
import { physioApi } from '../services/api';
import SlotAvailabilityPicker from './SlotAvailabilityPicker';

// Helper to calculate end time from start time and session duration (minutes)
const calculateEndTime = (startStr, durationMins = 50) => {
  if (!startStr) return '';
  const parts = startStr.split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const total = h * 60 + m + (Number(durationMins) || 50);
  const endH = Math.floor((total % (24 * 60)) / 60);
  const endM = total % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
};

// Helper to prepare full weekly schedule object
const prepareWeeklySchedule = (schedule, defaultDuration = 50) => {
  const prepared = {};
  const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  dayNames.forEach((d) => {
    const dayCfg = schedule?.[d] || { enabled: false, startTime: '09:00', duration: defaultDuration };
    const start = dayCfg.startTime || '09:00';
    const dur = Number(dayCfg.duration) || Number(defaultDuration) || 50;
    prepared[d] = {
      enabled: Boolean(dayCfg.enabled),
      startTime: start,
      duration: dur,
      endTime: dayCfg.endTime || calculateEndTime(start, dur),
    };
  });
  return prepared;
};

const TherapistSlotManager = ({ onSlotsUpdated }) => {
  const [slots, setSlots] = useState([]);
  const [consultationFee, setConsultationFee] = useState(999);
  const [consultationDuration, setConsultationDuration] = useState(50);
  const [durationFees, setDurationFees] = useState({
    30: 599,
    50: 999,
    80: 1499,
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingFee, setIsSavingFee] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [viewMode, setViewMode] = useState('picker'); // 'picker' | 'list'

  // Mode: 'single' | 'weekly'
  const [activeTab, setActiveTab] = useState('single');

  // Single Slot Form State
  const todayStr = new Date().toISOString().split('T')[0];
  const [slotForm, setSlotForm] = useState({
    date: todayStr,
    startTime: '10:00',
    duration: 50,
  });

  // Single Global Duration for Weekly Hours
  const [weeklyDuration, setWeeklyDuration] = useState(50);

  // Weekly Schedule State (Start time and duration per active day)
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: { enabled: true, startTime: '09:00', duration: 50 },
    tuesday: { enabled: true, startTime: '09:00', duration: 50 },
    wednesday: { enabled: true, startTime: '09:00', duration: 50 },
    thursday: { enabled: true, startTime: '09:00', duration: 50 },
    friday: { enabled: true, startTime: '09:00', duration: 50 },
    saturday: { enabled: true, startTime: '10:00', duration: 50 },
    sunday: { enabled: false, startTime: '00:00', duration: 50 },
  });
  const [autoGenerateWeeklySlots, setAutoGenerateWeeklySlots] = useState(true);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  };

  const calculateAutoTiers = (baseRate) => {
    const base = Number(baseRate) || 999;
    return {
      30: Math.round(base * 0.65),
      50: base,
      80: Math.round(base * 1.5),
    };
  };

  const handleWeeklyDurationChange = (dur) => {
    const durNum = Number(dur) || 50;
    setWeeklyDuration(durNum);
    setConsultationDuration(durNum);
    setWeeklySchedule(prev => {
      const updated = { ...prev };
      days.forEach(d => {
        if (updated[d]) {
          updated[d] = {
            ...updated[d],
            duration: durNum,
            endTime: calculateEndTime(updated[d].startTime || '09:00', durNum),
          };
        }
      });
      return updated;
    });
  };

  const fetchSlots = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const res = await physioApi.getSlots();
      if (res?.availableSlots) {
        setSlots(res.availableSlots);
      }
      if (res?.weeklySchedule && Object.keys(res.weeklySchedule).length > 0) {
        setWeeklySchedule(res.weeklySchedule);
        // Find existing duration if present
        const firstActiveDay = Object.values(res.weeklySchedule).find(v => v?.duration);
        if (firstActiveDay?.duration) {
          setWeeklyDuration(Number(firstActiveDay.duration) || 50);
        }
      }
      if (res?.consultationFee !== undefined) {
        setConsultationFee(res.consultationFee);
      }
      if (res?.consultationDuration !== undefined) {
        setConsultationDuration(res.consultationDuration);
        setWeeklyDuration(res.consultationDuration);
      }
      if (res?.durationFees && typeof res.durationFees === 'object') {
        setDurationFees({
          30: res.durationFees[30] || res.durationFees['30'] || Math.round((res.consultationFee || 999) * 0.65),
          50: res.durationFees[50] || res.durationFees['50'] || (res.consultationFee || 999),
          80: res.durationFees[80] || res.durationFees['80'] || Math.round((res.consultationFee || 999) * 1.5),
        });
      } else if (res?.consultationFee) {
        setDurationFees(calculateAutoTiers(res.consultationFee));
      }
      if (onSlotsUpdated) onSlotsUpdated(res?.availableSlots || []);
    } catch (err) {
      console.error('Failed to load slots:', err);
      setErrorMessage(err.message || 'Could not load your availability slots.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const handleBaseFeeChange = (val) => {
    setConsultationFee(val);
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      setDurationFees(prev => ({
        ...prev,
        50: num,
        30: prev[30] ? prev[30] : Math.round(num * 0.65),
        80: prev[80] ? prev[80] : Math.round(num * 1.5),
      }));
    }
  };

  const handleDurationFeeChange = (dur, val) => {
    setDurationFees(prev => ({
      ...prev,
      [dur]: Number(val) || 0,
    }));
    if (dur === 50) {
      setConsultationFee(Number(val) || 0);
    }
  };

  const handleResetAutoTiers = () => {
    const tiers = calculateAutoTiers(consultationFee);
    setDurationFees(tiers);
    setStatusMessage('✓ Recalculated duration fees based on standard base rate.');
    setTimeout(() => setStatusMessage(''), 3000);
  };

  const handleSaveFee = async (e) => {
    if (e) e.preventDefault();
    try {
      setIsSavingFee(true);
      setErrorMessage('');
      const feeNum = Number(consultationFee) || 0;
      const durNum = Number(consultationDuration) || 50;
      const formattedSchedule = prepareWeeklySchedule(weeklySchedule, durNum);
      const res = await physioApi.updateWeeklySchedule({
        weeklySchedule: formattedSchedule,
        consultationFee: feeNum,
        consultationDuration: durNum,
        durationFees: {
          30: Number(durationFees[30]) || Math.round(feeNum * 0.65),
          50: Number(durationFees[50]) || feeNum,
          80: Number(durationFees[80]) || Math.round(feeNum * 1.5),
        },
      });
      if (res?.weeklySchedule) {
        setWeeklySchedule(res.weeklySchedule);
      }
      if (res?.consultationFee !== undefined) {
        setConsultationFee(res.consultationFee);
      }
      if (res?.consultationDuration !== undefined) {
        setConsultationDuration(res.consultationDuration);
      }
      if (res?.durationFees) {
        setDurationFees(res.durationFees);
      }
      setStatusMessage('✓ Duration-based consultation fees and settings updated successfully!');
      setTimeout(() => setStatusMessage(''), 4000);
      await fetchSlots();
    } catch (err) {
      console.error('Failed to update fee:', err);
      setErrorMessage(err.message || 'Failed to update consultation fees & durations.');
    } finally {
      setIsSavingFee(false);
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!slotForm.date || !slotForm.startTime) {
      setErrorMessage('Please specify date and start time.');
      return;
    }

    const duration = Number(slotForm.duration) || Number(consultationDuration) || 50;
    const computedEndTime = calculateEndTime(slotForm.startTime, duration);
    const slotPrice = Number(durationFees[duration]) || (duration === 30 ? Math.round(consultationFee * 0.65) : duration === 80 ? Math.round(consultationFee * 1.5) : consultationFee);

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const res = await physioApi.addSlot({
        date: slotForm.date,
        startTime: slotForm.startTime,
        endTime: computedEndTime,
        duration: duration,
        price: slotPrice,
      });
      if (res?.availableSlots) {
        setSlots(res.availableSlots);
        if (onSlotsUpdated) onSlotsUpdated(res.availableSlots);
      }
      setStatusMessage(`✓ Slot for ${slotForm.date} at ${slotForm.startTime} (${duration} mins • ₹${slotPrice}) added successfully!`);
      setTimeout(() => setStatusMessage(''), 4000);
      await fetchSlots();
    } catch (err) {
      console.error('Failed to add slot:', err);
      setErrorMessage(err.message || 'Failed to add date-time slot.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSlot = async (slotId, slotObj) => {
    if (!window.confirm('Are you sure you want to remove this available slot?')) return;

    try {
      // Optimistic update so it immediately vanishes
      setSlots((prev) => prev.filter((s) => {
        if (slotId && s._id && String(s._id) === String(slotId)) return false;
        if (slotObj?.date && slotObj?.startTime && s.date === slotObj.date && s.startTime === slotObj.startTime) return false;
        return true;
      }));

      const res = await physioApi.deleteSlot(slotId, slotObj);
      if (res?.availableSlots) {
        setSlots(res.availableSlots);
        if (onSlotsUpdated) onSlotsUpdated(res.availableSlots);
      }
      setStatusMessage('✓ Slot removed successfully.');
      setTimeout(() => setStatusMessage(''), 3000);
      await fetchSlots();
    } catch (err) {
      console.error('Failed to delete slot:', err);
      setErrorMessage(err.message || 'Failed to delete slot.');
      await fetchSlots();
    }
  };

  const handleClearAllSlots = async () => {
    if (!window.confirm('Are you sure you want to clear ALL date-time slots? You can re-generate them at any time.')) return;
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const res = await physioApi.clearAllSlots();
      setSlots(res?.availableSlots || []);
      if (onSlotsUpdated) onSlotsUpdated(res?.availableSlots || []);
      setStatusMessage('✓ All date-time slots cleared successfully.');
      setTimeout(() => setStatusMessage(''), 4000);
      await fetchSlots();
    } catch (err) {
      console.error('Failed to clear slots:', err);
      setErrorMessage(err.message || 'Failed to clear slots.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveWeeklySchedule = async (generateSlotsNow = true) => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const duration = Number(weeklyDuration) || Number(consultationDuration) || 50;
      const feeNum = Number(consultationFee) || 0;
      const formattedSchedule = prepareWeeklySchedule(weeklySchedule, duration);
      const shouldGenerate = generateSlotsNow !== undefined ? Boolean(generateSlotsNow) : Boolean(autoGenerateWeeklySlots);
      const res = await physioApi.updateWeeklySchedule({
        weeklySchedule: formattedSchedule,
        consultationFee: feeNum,
        consultationDuration: duration,
        durationFees: {
          30: Number(durationFees[30]) || Math.round(feeNum * 0.65),
          50: Number(durationFees[50]) || feeNum,
          80: Number(durationFees[80]) || Math.round(feeNum * 1.5),
        },
        autoGenerateSlots: shouldGenerate,
        slotDurationMinutes: duration,
        daysAhead: 14,
      });
      if (res?.weeklySchedule) {
        setWeeklySchedule(res.weeklySchedule);
      }
      if (res?.availableSlots) {
        setSlots(res.availableSlots);
        if (onSlotsUpdated) onSlotsUpdated(res.availableSlots);
      }
      setStatusMessage(res?.message || '✓ Weekly schedule saved successfully!');
      setTimeout(() => setStatusMessage(''), 5000);
      await fetchSlots();
    } catch (err) {
      console.error('Failed to save weekly schedule:', err);
      setErrorMessage(err.message || 'Failed to save weekly schedule.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    const d = slot.date || 'Undated';
    if (!acc[d]) acc[d] = [];
    acc[d].push(slot);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedSlots).sort();

  return (
    <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginTop: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
            ⏰ Session Availability & Time Slots
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
            Add your available date and time slots. Clients can view and book these slots directly on your profile.
          </p>
        </div>
      </div>

      {/* Duration-Based Consultation Fees Configuration */}
      <div style={{
        background: 'linear-gradient(135deg, #f8faff 0%, #eff6ff 100%)',
        border: '1.5px solid #dbeafe',
        borderRadius: '16px',
        padding: '22px 24px',
        marginBottom: '24px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '18px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800 }}>
              ₹
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#1e3a8a' }}>
                Duration-Based Session Pricing
              </h4>
              <p style={{ margin: '3px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
                Set your consultation fees according to session durations. Clients pay based on the duration they choose.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleResetAutoTiers}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                background: '#ffffff',
                border: '1px solid #bfdbfe',
                color: '#2563eb',
                fontSize: '11.5px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
              title="Auto-calculate 30m and 80m from 50m rate"
            >
              ⚡ Auto-Calculate Tiers
            </button>
          </div>
        </div>

        <form onSubmit={handleSaveFee}>
          {/* 3 Duration Tiers Inputs Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: '14px',
            marginBottom: '18px'
          }}>
            {/* 30 Min Tier */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#0369a1', background: '#e0f2fe', padding: '2px 8px', borderRadius: '6px' }}>
                  ⏱️ 30 Mins
                </span>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Quick / Follow-up</span>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: '10px', fontWeight: 700, color: '#64748b', fontSize: '13px' }}>₹</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={durationFees[30] || ''}
                  onChange={(e) => handleDurationFeeChange(30, e.target.value)}
                  placeholder="599"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 10px 8px 24px',
                    borderRadius: '8px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 800,
                    color: '#0f172a'
                  }}
                />
              </div>
            </div>

            {/* 50 Min Standard Tier */}
            <div style={{
              background: '#ffffff',
              border: '2px solid #2563eb',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 4px 12px rgba(37,99,235,0.08)',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#1e3a8a', background: '#dbeafe', padding: '2px 8px', borderRadius: '6px' }}>
                  ⭐ 50 Mins (Standard)
                </span>
                <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: 700 }}>Recommended</span>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: '10px', fontWeight: 700, color: '#64748b', fontSize: '13px' }}>₹</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={durationFees[50] || ''}
                  onChange={(e) => {
                    handleDurationFeeChange(50, e.target.value);
                    handleBaseFeeChange(e.target.value);
                  }}
                  placeholder="999"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 10px 8px 24px',
                    borderRadius: '8px',
                    border: '1.5px solid #2563eb',
                    fontSize: '14px',
                    fontWeight: 800,
                    color: '#0f172a',
                    background: '#f8faff'
                  }}
                />
              </div>
            </div>

            {/* 80 Min Deep Tier */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#6b21a8', background: '#f3e8ff', padding: '2px 8px', borderRadius: '6px' }}>
                  💎 80 Mins
                </span>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Deep / Extended</span>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: '10px', fontWeight: 700, color: '#64748b', fontSize: '13px' }}>₹</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={durationFees[80] || ''}
                  onChange={(e) => handleDurationFeeChange(80, e.target.value)}
                  placeholder="1499"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 10px 8px 24px',
                    borderRadius: '8px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 800,
                    color: '#0f172a'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>✓ Client sees:</span>
              <strong style={{ background: '#dbeafe', padding: '2px 8px', borderRadius: '6px' }}>
                30m: ₹{durationFees[30]} | 50m: ₹{durationFees[50]} | 80m: ₹{durationFees[80]}
              </strong>
            </div>

            <button
              type="submit"
              disabled={isSavingFee}
              style={{
                padding: '9px 20px',
                borderRadius: '8px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(37,99,235,0.25)',
                transition: 'background 0.2s',
              }}
            >
              {isSavingFee ? 'Saving Tiers...' : '💾 Save Duration Pricing'}
            </button>
          </div>
        </form>
      </div>

      {statusMessage && (
        <div style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', fontWeight: 600 }}>
          {statusMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', fontWeight: 600 }}>
          {errorMessage}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', marginBottom: '18px', width: 'fit-content' }}>
        <button
          type="button"
          onClick={() => setActiveTab('single')}
          style={{
            padding: '7px 16px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'single' ? '#ffffff' : 'transparent',
            color: activeTab === 'single' ? '#6366f1' : '#64748b',
            boxShadow: activeTab === 'single' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
            transition: 'all 0.2s',
          }}
        >
          + Add Date & Time Slot
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('weekly')}
          style={{
            padding: '7px 16px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'weekly' ? '#ffffff' : 'transparent',
            color: activeTab === 'weekly' ? '#6366f1' : '#64748b',
            boxShadow: activeTab === 'weekly' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
            transition: 'all 0.2s',
          }}
        >
          📅 Weekly Hours
        </button>
      </div>

      {/* 1. SINGLE SLOT FORM WITH DURATION & FEE PREVIEW */}
      {activeTab === 'single' && (
        <form onSubmit={handleSingleSubmit} style={{ background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>Date *</label>
              <input
                type="date"
                min={todayStr}
                value={slotForm.date}
                onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                required
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>Start Time *</label>
              <input
                type="time"
                value={slotForm.startTime}
                onChange={(e) => setSlotForm({ ...slotForm, startTime: e.target.value })}
                required
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>Duration & Rate *</label>
              <select
                value={slotForm.duration || 50}
                onChange={(e) => setSlotForm({ ...slotForm, duration: Number(e.target.value) })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #bfdbfe', fontSize: '13px', fontWeight: 700, background: '#fff' }}
              >
                <option value={30}>⏱️ 30 Mins — ₹{durationFees[30] || 599}</option>
                <option value={50}>⭐ 50 Mins — ₹{durationFees[50] || 999}</option>
                <option value={80}>💎 80 Mins — ₹{durationFees[80] || 1499}</option>
              </select>
            </div>
          </div>

          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
            fontSize: '12.5px',
            color: '#1e40af'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>⏱️</span>
              <span>
                Slot time: <strong>{slotForm.startTime || '10:00'} - {calculateEndTime(slotForm.startTime || '10:00', slotForm.duration || 50)}</strong> ({slotForm.duration || 50} mins)
              </span>
            </span>
            <span style={{ fontWeight: 800, background: '#dbeafe', padding: '3px 10px', borderRadius: '8px', color: '#1e3a8a' }}>
              Fee: ₹{durationFees[slotForm.duration || 50] || consultationFee}
            </span>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '10px 22px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: '#ffffff',
              border: 'none',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)',
            }}
          >
            {isSubmitting ? 'Adding...' : '+ Add Slot with Duration Fee'}
          </button>
        </form>
      )}



      {/* 3. WEEKLY RECURRING SCHEDULE WITH SINGLE DURATION & RATE DROPDOWN */}
      {activeTab === 'weekly' && (
        <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          
          {/* Single Dropdown Header for Weekly Hours */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)',
            border: '1.5px solid #bfdbfe',
            padding: '16px 18px',
            borderRadius: '12px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '14px',
            boxShadow: '0 2px 8px rgba(37,99,235,0.06)'
          }}>
            <div style={{ minWidth: '220px', flex: '1 1 280px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#1e3a8a', marginBottom: '4px' }}>
                ⏱️ Weekly Session Duration & Rate *
              </label>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                Select duration & fees once: all weekly slots will be created based on this duration.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <select
                value={weeklyDuration}
                onChange={(e) => handleWeeklyDurationChange(e.target.value)}
                style={{
                  padding: '9px 14px',
                  borderRadius: '10px',
                  border: '2px solid #2563eb',
                  fontSize: '13.5px',
                  fontWeight: 800,
                  background: '#ffffff',
                  color: '#1e3a8a',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37,99,235,0.12)',
                  minWidth: '230px'
                }}
              >
                <option value={30}>⏱️ 30 Mins — ₹{durationFees[30] || 599}</option>
                <option value={50}>⭐ 50 Mins — ₹{durationFees[50] || 999} (Standard)</option>
                <option value={80}>💎 80 Mins — ₹{durationFees[80] || 1499}</option>
              </select>

              <div style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '7px 12px',
                borderRadius: '8px',
                fontWeight: 800,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>Applied Rate:</span>
                <span style={{ fontSize: '13.5px', color: '#1e3a8a' }}>₹{durationFees[weeklyDuration] || consultationFee}</span>
                <span style={{ fontSize: '11px', color: '#475569', fontWeight: 600 }}>/ {weeklyDuration}m slot</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
            {days.map((day) => {
              const cfg = weeklySchedule[day] || { enabled: false, startTime: '09:00', duration: weeklyDuration };
              const currentDur = weeklyDuration;
              const currentFee = durationFees[currentDur] || consultationFee;

              return (
                <div
                  key={day}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: cfg.enabled ? '1.5px solid #dbeafe' : '1px solid #e2e8f0',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={cfg.enabled}
                      onChange={(e) =>
                        setWeeklySchedule({
                          ...weeklySchedule,
                          [day]: { ...cfg, enabled: e.target.checked },
                        })
                      }
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 700, fontSize: '13.5px', color: cfg.enabled ? '#1e293b' : '#94a3b8' }}>
                      {dayLabels[day]}
                    </span>
                  </label>

                  {cfg.enabled ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {/* Start Time Input */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Start Time:</span>
                        <input
                          type="time"
                          value={cfg.startTime || '09:00'}
                          onChange={(e) =>
                            setWeeklySchedule({
                              ...weeklySchedule,
                              [day]: { ...cfg, startTime: e.target.value },
                            })
                          }
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12.5px', fontWeight: 600 }}
                        />
                      </div>

                      {/* Computed Slot Preview Badge */}
                      <span style={{ fontSize: '12px', color: '#1e40af', background: '#eff6ff', padding: '5px 12px', borderRadius: '6px', fontWeight: 700, border: '1px solid #dbeafe' }}>
                        ⏱️ Slot: {cfg.startTime || '09:00'} - {calculateEndTime(cfg.startTime || '09:00', weeklyDuration)} ({weeklyDuration} mins) • ₹{currentFee}
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>Closed / Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Auto-generate options */}
          <div style={{ background: '#f1f5f9', padding: '14px 16px', borderRadius: '10px', marginBottom: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoGenerateWeeklySlots}
                onChange={(e) => setAutoGenerateWeeklySlots(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                ⚡ Auto-generate active booking slots for the next 14 days using {weeklyDuration}m (₹{durationFees[weeklyDuration] || consultationFee})
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleSaveWeeklySchedule(autoGenerateWeeklySlots)}
              disabled={isSubmitting}
              style={{
                padding: '10px 22px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)',
              }}
            >
              {isSubmitting ? 'Saving...' : '💾 Save Weekly Schedule'}
            </button>

            <button
              type="button"
              onClick={() => handleSaveWeeklySchedule(true)}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
              }}
            >
              ⚡ Generate Next 14 Days Slots Now
            </button>
          </div>
        </div>
      )}

      {/* ACTIVE SLOTS OVERVIEW / CHECK AVAILABLE SLOTS */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#1e293b' }}>
              📅 Configured Availability ({slots.length} Slots)
            </h4>
            <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', gap: '4px' }}>
              <button
                type="button"
                onClick={() => setViewMode('picker')}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: viewMode === 'picker' ? '#ffffff' : 'transparent',
                  color: viewMode === 'picker' ? '#065f46' : '#64748b',
                  boxShadow: viewMode === 'picker' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                ✨ Interactive Slot View
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: viewMode === 'list' ? '#ffffff' : 'transparent',
                  color: viewMode === 'list' ? '#1e293b' : '#64748b',
                  boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                📋 Grouped List
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {slots.length > 0 && (
              <button
                type="button"
                onClick={handleClearAllSlots}
                disabled={isSubmitting}
                style={{ border: 'none', background: '#fee2e2', color: '#dc2626', fontSize: '12px', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer' }}
              >
                🗑️ Clear All Slots
              </button>
            )}
            <button
              type="button"
              onClick={fetchSlots}
              style={{ border: 'none', background: 'none', color: '#6366f1', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
            ⏳ Loading your slots...
          </div>
        ) : viewMode === 'picker' ? (
          <SlotAvailabilityPicker
            slots={slots}
            weeklySchedule={weeklySchedule}
            consultationDuration={consultationDuration}
            isTherapistManager={true}
            onDeleteSlot={handleDeleteSlot}
            title="Check available slots"
          />
        ) : slots.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 700, color: '#334155' }}>
              No custom date-time slots created yet.
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
              Select a date and start/end time above to open up appointment slots for clients.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {sortedDates.map((d) => (
              <div key={d} style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px 18px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '15px' }}>🗓️</span>
                  <strong style={{ fontSize: '14px', color: '#0f172a' }}>
                    {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                  </strong>
                  <span style={{ fontSize: '11px', background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
                    {groupedSlots[d].length} slot{groupedSlots[d].length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {groupedSlots[d].map((slot) => {
                    const isBooked = slot.isBooked;
                    return (
                      <div
                        key={slot._id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: isBooked ? '#fef2f2' : '#ffffff',
                          border: isBooked ? '1px solid #fecaca' : '1px solid #cbd5e1',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        }}
                      >
                        <span style={{ fontWeight: 700, color: isBooked ? '#991b1b' : '#1e293b' }}>
                          ⏰ {slot.startTime} - {slot.endTime}
                        </span>
                        {isBooked ? (
                          <span style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>
                            Booked
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeleteSlot(slot._id)}
                            title="Delete Slot"
                            style={{
                              border: 'none',
                              background: '#fee2e2',
                              color: '#dc2626',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              cursor: 'pointer',
                              fontWeight: 700,
                              fontSize: '11px',
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistSlotManager;
